import axiosInstance from "./axiosConfig";

const WEB_PUSH_SUBSCRIBE_PATH = "/api/admin/web-push/subscribe";

export type PushNotificationStatus =
  | "checking"
  | "unsupported"
  | "denied"
  | "enabled"
  | "disabled"
  | "missing-key";

export type PushNotificationState = {
  status: PushNotificationStatus;
  message: string;
};

function webPushPublicKey(): string {
  return process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY || "";
}

export function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray.buffer.slice(
    outputArray.byteOffset,
    outputArray.byteOffset + outputArray.byteLength
  );
}

export function isPushNotificationSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

async function existingServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

async function activeServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  const registration = await existingServiceWorkerRegistration();
  if (registration) return registration;

  throw new Error(
    "Service worker is not ready yet. Use a production build or refresh after the app finishes loading."
  );
}

export async function getPushNotificationState(): Promise<PushNotificationState> {
  if (!isPushNotificationSupported()) {
    return {
      status: "unsupported",
      message: "Push notifications are not supported in this browser.",
    };
  }

  if (!webPushPublicKey()) {
    return {
      status: "missing-key",
      message: "Web push public key is not configured.",
    };
  }

  if (Notification.permission === "denied") {
    return {
      status: "denied",
      message: "Notifications are blocked in this browser.",
    };
  }

  const registration = await existingServiceWorkerRegistration();
  const subscription = await registration?.pushManager.getSubscription();

  if (subscription) {
    return {
      status: "enabled",
      message: "Push notifications are enabled for this browser.",
    };
  }

  return {
    status: "disabled",
    message: "Push notifications are disabled for this browser.",
  };
}

/**
 * Subscribes this browser and registers the subscription with the backend.
 *
 * Takes no token: the session is an httpOnly cookie that axiosInstance sends automatically.
 * Callers gate on `isAuthenticated` — an unauthenticated call just 401s.
 */
export async function subscribeAdminToPush(): Promise<PushSubscription> {
  if (!isPushNotificationSupported()) {
    throw new Error("Push notifications are not supported in this browser.");
  }

  const publicKey = webPushPublicKey();
  if (!publicKey) {
    throw new Error("Web push public key is not configured.");
  }

  if (Notification.permission === "denied") {
    throw new Error("Notifications are blocked in this browser.");
  }

  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();

  if (permission !== "granted") {
    throw new Error("Notification permission was not granted.");
  }

  const registration = await activeServiceWorkerRegistration();
  const existingSubscription = await registration.pushManager.getSubscription();
  const subscription =
    existingSubscription ||
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  // axiosInstance carries the session cookie; no token is passed in any more.
  await axiosInstance.post(WEB_PUSH_SUBSCRIBE_PATH, subscription.toJSON());

  return subscription;
}

export async function unsubscribeAdminFromPush(): Promise<boolean> {
  if (!isPushNotificationSupported()) return false;

  const registration = await existingServiceWorkerRegistration();
  const subscription = await registration?.pushManager.getSubscription();

  if (!subscription) return false;
  return subscription.unsubscribe();
}
